import React, { useState, useRef, useEffect } from "react";
import { Link as NavLink, withRouter } from "react-router-dom";
import useOnClickOutside from "utils/hooks/useOnClickOutside";
import { useSpring, animated } from "react-spring";
import { useMeasure } from "react-use";
import { useCountryValue } from "stores/country";
import styled from "styled-components";
import tw from "tailwind.macro";
import breakpoints from "utils/breakpoints";
import { isSitecoreEditorMode } from "utils/sitecoreEditorMode";
import MenuIcon from "components/icons/Menu";
import cx from "classnames";
import { Container } from "components/shared";
import { getFieldValue } from "@sitecore-jss/sitecore-jss-react";
import LeftArrowIcon from "components/icons/LeftArrow";
import HeartIcon from "components/icons/Heart";
import HeartFilledIcon from "components/icons/HeartFilled";
import useScrollDirection from "utils/hooks/useScrollDirection";
import pageTypes from "utils/pageTypes";
import { useUiValue } from "stores/uiContext";
import useBreakpoint from "utils/hooks/useBreakpoint";
import { Helmet } from "react-helmet";
import { getFirstContentElement } from "utils/getFirstContentElement";
import { useLocation } from "react-router-dom";
import { scrollToSection } from "utils/scrollTo";
import { scrollToTop } from "utils/scrollTo";
import ChevronDownIcon from "components/icons/ChevronDown";
import useDynamicsForm from "utils/hooks/useDynamicsForm";
import useAtlasRTXChatbot from "utils/hooks/useAtlasRTXChatbot";
import ReactDOM from "react-dom";
import { getMobileOS } from "utils/getMobileOS";

import loadable from "@loadable/component";
const CountrySelect = loadable(() =>
  import("components/components/atoms/CountrySelect")
);
const SkipToComponent = loadable(() =>
  import("components/components/molecules/SkipToComponent")
);
const ScrollToTopButton = loadable(() =>
  import("components/components/atoms/ScrollToTopButton")
);

const COMPONENTS_HEIGHT = {
  gallery: 100,
  contact: 60,
  sitemap: 80,
  whatsnearby: 40,
  amenities: 60,
};
const HEADER_EXPANDED_HEIGHT = "6.875rem";
const HEADER_EXPANDED_HEIGHT_ON_TOP = "10.5rem";
const HEADER_COLLAPSED_HEIGHT = "5rem";

const HeaderContainer = styled(animated.header)`
  transition: all 0.1s linear;

  ${({ isSitecoreEditor }) =>
    isSitecoreEditor ? `top: unset; position: inherit;` : `top: 0;`}
  ${({ isScrollOnEditorPage }) =>
    isScrollOnEditorPage
      ? `top: 8.688rem; position: fixed;`
      : ``}
  transform: translate3d(0, 0, 0);
`;

const HeaderPlaceholder = styled.div`
  height: ${HEADER_EXPANDED_HEIGHT};
`;

const StyledContainer = styled(Container)`
  @media ${breakpoints.lg} {
    padding: 0 1.875rem;
  }
  height: ${({ fixed }) =>
    !fixed ? HEADER_EXPANDED_HEIGHT : HEADER_COLLAPSED_HEIGHT};
`;

const Logo = styled.img`
  height: 3.3rem;

  @media ${breakpoints.lg} {
    height: 4.1rem;
  }
`;

const BlueBar = styled.div`
  height: 0.125rem;
`;

const NavButton = styled.button`
  font-size: 1.0625rem; 
  min-width: 3.375rem;  
  ${({ active }) => active && tw``}

  &:hover ~ ${BlueBar} {
    display: block;
  }
`;

const Link = styled(NavLink)`
  font-size: 1.0625rem;
  text-decoration: none !important;

  &:hover ~ ${BlueBar} {
    display: block;
  }
`;
const LeftIconCity = styled.div`
  position: absolute;
  display: flex;
  opacity: 0;
  align-items: center;
  right: 0;
  bottom: ${({ isState }) => (isState ? `0.25rem` : `0`)};
  top: 0;
  transition: all 0.3s ease-out;
`;

const City = styled.a`
  ${tw`flex font-graphie text-base leading-6 text-white py-1 tracking-normal font-light relative`}
  &:hover {
    font-weight: bold;
  }
  &:hover ${LeftIconCity} {
    opacity: 1;
    right: -1.5rem;
  }
`;

const State = styled.a`
  ${tw`flex font-graphie text-base leading-6 text-white py-1 tracking-normal font-light relative`}
  &:hover {
    font-weight: bold;
  }
  &:hover ${LeftIconCity} {
    opacity: 1;
    right: -1.5rem;
  }
`;

const CityList = styled.div`
  margin-top: 0.5rem;
  display: inline-grid;
  column-gap: 5rem;
`;

const CityListContainer = styled(animated.div)`
  z-index: 35;
  top: 100%;
`;

const CitiesContainer = styled(Container)`
  padding-left: 20%;
  padding-right: 5%;
`;

const StateContainer = styled.div`
  flex: 1;
  scroll-snap-align: start;
  max-width: 220px;

  &:nth-child(2) {
    margin: 0;
  }
`;

const HeartFilled = styled(HeartFilledIcon)`
  transition: all 0.2s ease-in-out;
  &:hover {
    transform: scale(1.2);
  }
`;

const RectangleSeparator = styled.span`
  height: 0.125rem;
`;

const CountrySelectText = styled.p`
  font-size: 1.063rem;
  letter-spacing: 0.64px;
`;

const SecondaryNavBar = styled(animated.div)`
  z-index: 35;
  top: 100%;
`;

const SubNavigationOpt = styled.a`
  padding: 1.75rem;
`;
//width: 7.25rem;
const CountrySelectContainer = styled.div`
  width: 5.625rem;
`;

const mapNavigationItems = (items, country) => {
  if (items.length > 0) {
    const navigationItems = items.find((e) => e.name.includes(country));
    if (navigationItems) {
      if (!Array.isArray(navigationItems.navigationItems)) return [];
      return navigationItems.navigationItems;
    }
  }
  return [];
};

const removehideMap = (href) => {
  return href.replace("hideMap=false", "hideMap=true");
};

const HEADER_POSITION_VISIBLE = 0;
const HEADER_POSITION_HIDDEN = -80;

const Header = ({ fields }) => {
  //console.log({ fields });
  const [
    {
      notificationActive,
      nationalNotificationActive,
      notificationHeight,
      nationalNotificationHeight,
    },
  ] = useUiValue();

  const [hover, setHover] = useState(false);
  const [heartFilled, setHeartFilled] = useState(false);

  const [fixed, setFixed] = useState(false);
  const [isTop, setIsTop] = useState(true);

  const [headerPosition, setHeaderPosition] = useState(HEADER_POSITION_VISIBLE);

  const [{ country }] = useCountryValue();
  const isSitecoreEditor = isSitecoreEditorMode();
  // Fields
  const logoAlt = getFieldValue(fields, "logoAlt");
  const logoSrc = getFieldValue(fields, "logoSrc");
  const citiesField = getFieldValue(fields, "cities");
  const currentPageType = getFieldValue(fields, "currentPageType");
  const goToFavorites = getFieldValue(fields, "goToFavorites");
  const mouseflowTrackingURL = getFieldValue(fields, "mouseflowTrackingURL");
  const dynaMXFormID = getFieldValue(fields, "dynaMXFormID");
  const dynaMXFormAPIURL = getFieldValue(fields, "dynaMXFormAPIURL");
  const { updateDynaMXFormID, updateDynaMXFormAPIURL } = useDynamicsForm();
  const dynamicsScriptsCodeRef = useRef();

  useEffect(() => {
    if (dynaMXFormID && dynaMXFormAPIURL) {
      updateDynaMXFormID(dynaMXFormID);
      updateDynaMXFormAPIURL(dynaMXFormAPIURL);
    } else {
      updateDynaMXFormID("");
      updateDynaMXFormAPIURL("");
    }
  }, [dynaMXFormID, dynaMXFormAPIURL]);

  const dynamicsChatbot = getFieldValue(fields, "dynamicsChatbot");
  const enableChatbot = dynamicsChatbot ? dynamicsChatbot.enableChatbot : false;
  const chatbotScript = dynamicsChatbot ? dynamicsChatbot.chatbotScript : "";

  useEffect(() => {
    if (
      enableChatbot &&
      chatbotScript &&
      typeof window !== "undefined" &&
      document
    ) {
      const snipped = document
        .createRange()
        .createContextualFragment(chatbotScript);
      document.head.appendChild(snipped);
    }
  }, [chatbotScript, enableChatbot]);

  const atlasRTXChatbotScript = getFieldValue(fields, "atlasRTXChatbotScript");

  const { updateAtlasRTXChatbotScript } = useAtlasRTXChatbot();
  useEffect(() => {
    if (atlasRTXChatbotScript && typeof window !== "undefined" && document) {
      const scriptList = document.querySelectorAll(
        "script[type='text/javascript']"
      );
      const convertedNodeList = Array.from(scriptList);
      const testScript = convertedNodeList.find((script) =>
        script.innerText.includes("atlasSettings")
      );
      if (testScript) testScript.parentNode.removeChild(testScript);

      const atlasChatDiv = document.getElementById("iAtlasChatDiv");
      if (atlasChatDiv) atlasChatDiv.remove();

      if (currentPageType === pageTypes.MetroSearch) {
        //console.log({ atlasRTXChatbotScript });
        const checkEnv = atlasRTXChatbotScript.replace('"env":"",', "");
        updateAtlasRTXChatbotScript(checkEnv);
      } else {
        if (country === "USA" && currentPageType !== pageTypes.SupportPage) {
          //console.log({ atlasRTXChatbotScript });
          const checkEnv = atlasRTXChatbotScript.replace('"env":"",', "");
          const snipped = document
            .createRange()
            .createContextualFragment(checkEnv);
          if (snipped) document.head.appendChild(snipped);
        }
      }
    }
  }, [atlasRTXChatbotScript, currentPageType, country]);

  const navigationItems = mapNavigationItems(
    getFieldValue(fields, "navigationItems"),
    country
  );
  const cities = Array.isArray(citiesField)
    ? citiesField.filter((c) => c.country === country)
    : [];
  const [citiesVisible, toggleCities] = useState(false);
  const [citiesVisibleInDrawer, toggleCitiesInDrawer] = useState(false);
  const [secondaryNavBarOption, setSecondaryNavBarOption] = useState();
  const [menuOptionSelected, setMenuOptionSelected] = useState();
  const [secondaryNavBarVisible, toggleSecondaryNavBar] = useState(null);
  const [
    secondaryNavBarVisibleInDrawer,
    toggleSecondaryNavBarInDrawer,
  ] = useState(false);
  const [expandedOption, setExpandedOption] = useState();
  const [options, setOptions] = useState();
  const [isRightDrawerOpen, toggleRightDrawer] = useState(false);
  const [isLeftDrawerOpen, toggleLeftDrawer] = useState(false);
  const [isScrollOnEditorPage, setIsScrollOnEditorPage] = useState(false);
  const scrollDirection = useScrollDirection();
  const isWhiteHeader =
    hover ||
    fixed ||
    citiesVisible ||
    secondaryNavBarVisible ||
    [
      pageTypes.PlanPage,
      pageTypes.CondoPlan,
      pageTypes.MetroSearch,
      pageTypes.CommonPage,
      pageTypes.SupportPage,
      pageTypes.QmiPage,
      pageTypes.PromoPage,
      pageTypes.ContactUs,
      pageTypes.JanePage,
      pageTypes.AboutUs,
    ].includes(currentPageType);

  const isRelativeHeader = [
    pageTypes.PlanPage,
    pageTypes.CondoPlan,
    pageTypes.MetroSearch,
    pageTypes.CommonPage,
    pageTypes.SupportPage,
    pageTypes.QmiPage,
    pageTypes.PromoPage,
    pageTypes.ContactUs,
    pageTypes.JanePage,
    pageTypes.AboutUs,
  ].includes(currentPageType);

  const isMetroSearchHeader = currentPageType === pageTypes.MetroSearch;

  const breakpoint = useBreakpoint(
    typeof window !== "undefined" && window.innerWidth
  );
  const isMobile = ["sm", "xs", "md"].includes(breakpoint);

  // Animations
  const secondaryNavBarAnimationProps = useSpring({
    config: { duration: 100 },
    transform: secondaryNavBarVisible ? "translateY(0)" : "translateY(-400px)",
    opacity: secondaryNavBarVisible ? 1 : 0,
  });
  const cityListanimationProps = useSpring({
    config: { duration: 100 },
    transform: citiesVisible ? "translateY(0)" : "translateY(-400px)",
    opacity: citiesVisible ? 1 : 0,
    pointerEvents: citiesVisible ? "all" : "none",
  });
  const rightDrawerAnimationProps = useSpring({
    config: { duration: 100 },
    zIndex: 60,
    left: isRightDrawerOpen ? "0vw" : "100vw",
    overscrollBehavior: "none",
  });

  /*console.log({
    headerPosition,
    notificationHeight,
    nationalNotificationHeight, fixed
  });*/
  const headerStyle = useSpring({
    config: { duration: 10 },
    top: isMetroSearchHeader
      ? HEADER_POSITION_VISIBLE +
        notificationHeight +
        nationalNotificationHeight
      : isMobile
      ? fixed && scrollDirection === "up"
        ? HEADER_POSITION_VISIBLE +
          notificationHeight +
          nationalNotificationHeight
        : isTop
        ? HEADER_POSITION_VISIBLE
        : HEADER_POSITION_HIDDEN
      : fixed
      ? scrollDirection === "up" || citiesVisible || secondaryNavBarVisible
        ? HEADER_POSITION_VISIBLE +
          notificationHeight +
          nationalNotificationHeight
        : headerPosition
      : HEADER_POSITION_VISIBLE,
  });

  const defaultHeight = "0px";
  const [contentHeight, setContentHeight] = useState(defaultHeight);
  const [optionsHeight, setOptionsHeight] = useState(defaultHeight);
  const [citiesRefHeight, { height }] = useMeasure();
  const [optionsRefHeight, { height: optHeight }] = useMeasure();
  const getProperHeight = () => {
    if (citiesVisible || citiesVisibleInDrawer) {
      return `${contentHeight}px`;
    }
    return defaultHeight;
  };
  const getOptionsHeight = () => {
    if (secondaryNavBarVisibleInDrawer) {
      return `${optionsHeight}px`;
    }
    return defaultHeight;
  };
  const expand = useSpring({
    height: getProperHeight(),
    pointerEvents: citiesVisible || citiesVisibleInDrawer ? "all" : "none",
  });
  const expandOptions = useSpring({
    height: getOptionsHeight(),
  });
  const fade = useSpring({
    opacity: citiesVisible || citiesVisibleInDrawer ? 1 : 0,
    delay: citiesVisible || citiesVisibleInDrawer ? 200 : 0,
  });
  const fadeOptions = useSpring({
    opacity: secondaryNavBarVisibleInDrawer ? 1 : 0,
    delay: secondaryNavBarVisibleInDrawer ? 200 : 0,
  });

  useEffect(() => {
    setContentHeight(height);
    window.addEventListener("resize", setContentHeight(height));

    return window.removeEventListener("resize", setContentHeight(height));
  }, [height]);
  useEffect(() => {
    setOptionsHeight(optHeight);
    window.addEventListener("resize", setOptionsHeight(optHeight));

    return window.removeEventListener("resize", setOptionsHeight(optHeight));
  }, [optHeight]);

  const renderCities = () => (
    <animated.div
      className="flex flex-col items-center justify-start w-full"
      ref={citiesRefHeight}
      style={fade}
    >
      <>
        {cities.map((state, index) => (
          <div
            key={state.state}
            className={cx("flex flex-col w-full px-2", {
              "mb-4": index === cities.length - 1,
            })}
          >
            <h3 className="font-graphie text-mattamy-blue-light text-lg pt-2 font-bold">
              <a
                href={isMobile ? removehideMap(state.href) : state.href}
                onClick={() => toggleCities(false)}
                key={state.state}
                aria-label={state.state}
              >
                {state.state}
              </a>
            </h3>
            {state.cities.map((city) => (
              <a
                className={cx(
                  "mx-10-mattamy text-mattamy-blue-light font-graphie px-4 py-1 text-base"
                )}
                rel="prefetch"
                href={isMobile ? removehideMap(city.href) : city.href}
                key={city.name}
              >
                {city.name}
              </a>
            ))}
            {index !== cities.length - 1 && (
              <RectangleSeparator className="w-full bg-light-blue my-4" />
            )}
          </div>
        ))}
      </>
    </animated.div>
  );

  const renderOptions = (options) => (
    <animated.div
      className="flex flex-col items-center justify-start w-full"
      ref={optionsRefHeight}
      style={fadeOptions}
    >
      <>
        {options.map((option) => (
          <div
            key={option.navigationTitle}
            className="flex flex-col w-full px-2"
          >
            <a
              className="font-trade-gothic-20 text-xl text-mattamy-blue py-4 font-bold leading-normal"
              rel="prefetch"
              href={option.redirectUrl}
              aria-label={option.navigationTitle}
            >
              {option.navigationTitle}
            </a>
          </div>
        ))}
      </>
    </animated.div>
  );

  // Click outside functionality
  const citiesAndSecondaryNavBarRef = useRef();
  useOnClickOutside(citiesAndSecondaryNavBarRef, () => {
    toggleCities(false);
    toggleSecondaryNavBar(false);
    setHover(false);
    //setExpandedOption(!expandedOption);
  });

  //code to tab focus first link on submenu (useref to reference first link in states and other submenu links)
  useEffect(() => {
    if (citiesVisible && stateItemRef.current[0]) {
      stateItemRef.current[0].focus();
    }
  }, [citiesVisible]);

  useEffect(() => {
    if (secondaryNavBarVisible && firstLinkRef.current) {
      firstLinkRef.current.focus(); //focus on first link when dropdown is opened.
    }
  }, [secondaryNavBarVisible]);

  useEffect(() => {
    const closing = isRightDrawerOpen || isLeftDrawerOpen;
    if (closing) {
      toggleCities(false);
    }
  }, [isRightDrawerOpen, isLeftDrawerOpen]);

  const handleOnScrollInEditorPage = () => {
    if (isSitecoreEditor) {
      var header = document.getElementById("header");
      var sticky = header.offsetTop;
      if (window.pageYOffset > sticky) {
        setIsScrollOnEditorPage(true);
      } else {
        setIsScrollOnEditorPage(false);
      }
    }
  };

  useEffect(() => {
    window.addEventListener("scroll", handleOnScrollInEditorPage);
    return () =>
      window.removeEventListener("scroll", handleOnScrollInEditorPage);
  }, [handleOnScrollInEditorPage]);

  useEffect(() => {
    const handleScroll = () => {
      if (citiesVisible) {
        toggleCities(false);
      }
      if (secondaryNavBarVisible) {
        setSecondaryNavBarOption();
        setExpandedOption("");
        toggleSecondaryNavBar(false);
      }

      if (isMetroSearchHeader) {
        return;
      }

      const currentScrollY = window.scrollY;
      const offset =
        notificationActive || nationalNotificationActive
          ? notificationActive && !nationalNotificationActive
            ? 160
            : !notificationActive && nationalNotificationActive
            ? 320
            : 380
          : 200;
      if (notificationActive || nationalNotificationActive) {
        setIsTop(currentScrollY === 0);
      } else {
        setIsTop(true);
      }
      if (
        currentScrollY > offset &&
        !citiesVisible &&
        !secondaryNavBarVisible
      ) {
        setHeaderPosition(HEADER_POSITION_HIDDEN);
        setTimeout(() => {
          if (window.scrollY > offset) {
            setFixed(true);
          }
        }, 200);
      } else if (currentScrollY < offset) {
        setFixed(false);
        setTimeout(() => {
          setHeaderPosition(HEADER_POSITION_VISIBLE);
        }, 30);
        if (currentScrollY <= 0) {
          setFixed(false);
        }
      }
    };
    typeof window !== "undefined" &&
      window.addEventListener("scroll", handleScroll, { passive: true });
    return () =>
      typeof window !== "undefined" &&
      window.removeEventListener("scroll", handleScroll);
  }, [fixed, citiesVisible, secondaryNavBarVisible, isTop]);

  const isToggleCities = (item) => item.redirectUrl === "/search";

  const istoggleSecondaryNavBar = (title) => {
    if (title === secondaryNavBarOption || !secondaryNavBarOption) {
      toggleSecondaryNavBar(!secondaryNavBarVisible);
    } else {
      if (!secondaryNavBarVisible)
        toggleSecondaryNavBar(!secondaryNavBarVisible);
    }
    setSecondaryNavBarOption(title);
  };

  const citiesListRef = useRef();
  const secondNavRef = useRef();
  const stateItemRef = useRef([]);
  const cityItemRef = useRef([]);
  const navBarRef = useRef();
  const firstLinkRef = useRef();
  const [navigationWidth, setNavigationWidth] = useState(0);
  const [toMobile, setToMobile] = useState(false);
  let countStat = 0;
  useEffect(() => {
    if (navBarRef) {
      const current = navBarRef.current;
      let navigationItemsWitdh = 0;
      if (current) {
        for (let i = 0; i < current.childElementCount; i++) {
          navigationItemsWitdh =
            current.children[i].clientWidth + navigationItemsWitdh;
        }
      }
      setNavigationWidth(navigationItemsWitdh);
      const deviceWidth = typeof window !== "undefined" && window.innerWidth;
      if (deviceWidth && navigationWidth) {
        setToMobile(deviceWidth - navigationWidth < 300);
      }
    }
  }, [navBarRef, navigationWidth]);

  const focusNextMenuOption = () => {
    if (menuOptionSelected) {
      const target = document.getElementById(menuOptionSelected);
      if (target) target.focus();
    } else if (navBarRef && navBarRef.current) navBarRef.current.focus();
  };

  const location = useLocation();
  const hash = location.hash;
  useEffect(() => {
    if (hash)
      setTimeout(() => {
        const heightScroll = COMPONENTS_HEIGHT[hash.substring(1)] || 80;
        scrollToSection(hash.substring(1), heightScroll);
      }, 500);
  }, [hash]);
  const [isDomReady, setIsDomReady] = useState(false);
  useEffect(() => {
    setIsDomReady(true);
  }, []);
  useEffect(() => {
    if (typeof window !== "undefined" && document) {
      const body = document.querySelector("body");
      if (body) {
        if (isRightDrawerOpen) {
          body.classList.add("overflow-hidden");
          if (body.style.overflowY) body.style.overflowY = "hidden";
        } else {
          body.classList.remove("overflow-hidden");
          if (body.style.overflowY) body.style.overflowY = "auto";
        }
      }
    }
  }, [isRightDrawerOpen]);

  const isIOS = getMobileOS() === "iOS";
  useEffect(() => {
    if (typeof window !== "undefined" && document) {
      const mobileMenu = document.getElementById("mobileNavigationItems");
      if (mobileMenu) {
        if (isIOS) {
          if (citiesVisibleInDrawer) {
            mobileMenu.style.height = "175%";
          } else {
            if (secondaryNavBarVisibleInDrawer) {
              mobileMenu.style.height = "150%";
            } else mobileMenu.style.height = "100%";
          }
        } else {
          if (citiesVisibleInDrawer) {
            if (country === "USA") mobileMenu.style.height = "175%";
            else mobileMenu.style.height = "100%";
          } else {
            mobileMenu.style.height = "100%";
          }
        }
      }
    }
  }, [citiesVisibleInDrawer, secondaryNavBarVisibleInDrawer]);
  return (
    <>
      {isDomReady &&
        ReactDOM.createPortal(
          <SkipToComponent />,
          document.getElementById("skip-button")
        )}
      {isRelativeHeader && <HeaderPlaceholder></HeaderPlaceholder>}

      <div ref={citiesAndSecondaryNavBarRef}>
        <HeaderContainer
          // role="menubar"
          //tabIndex="-1"
          style={headerStyle}
          id="header"
          className={cx(
            "flex items-center justify-between lg:justify-start w-screen z-30 md:z-40",
            {
              "z-40": isTop || !isMobile,
              "z-30": isMobile && !isTop,
              "bg-white": isWhiteHeader,
              fixed:
                isMetroSearchHeader ||
                fixed ||
                ((citiesVisible || secondaryNavBarVisible) && !isTop) ||
                (!(notificationActive || nationalNotificationActive) &&
                  (secondaryNavBarVisible || citiesVisible) &&
                  isTop),
              absolute:
                (!isMetroSearchHeader &&
                  !fixed &&
                  !citiesVisible &&
                  !secondaryNavBarVisible) ||
                (!isMetroSearchHeader &&
                  isTop &&
                  (notificationActive || nationalNotificationActive)),
            }
          )}
          isSitecoreEditor={isSitecoreEditor}
          isScrollOnEditorPage={isScrollOnEditorPage}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() =>
            setHover(citiesVisible || secondaryNavBarVisible || false)
          }
        >
          <StyledContainer
            fixed={fixed}
            className={cx(
              "flex items-center justify-between lg:max-w-full lg:px-0 z-40",
              {
                "transition-all duration-200": isMetroSearchHeader,
                "bg-white": isWhiteHeader,
              }
            )}
          >
            <a
              className="h-full flex items-center justify-center"
              rel="prefetch"
              href="/"
              tabIndex="-1"
              aria-label={logoAlt + ". Go to HomePage"}
              onKeyDown={(event) => {
                if (event.key === "Tab" && event.shiftKey) {
                  event.preventDefault();
                  const notificationBannerElement = document.getElementById(
                    "national-notification-banner"
                  );
                  const focusableElements = notificationBannerElement.querySelectorAll(
                    'a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])'
                  );
                  if (focusableElements.length > 0) {
                    focusableElements[focusableElements.length - 1].focus();
                  }
                }
              }}
            >
              <figure>
                <Logo
                  id="logo-index"
                  src={logoSrc}
                  alt={logoAlt}
                  tabIndex="0"
                />
              </figure>
            </a>
            <nav
              className={cx(
                "hidden mx-1 items-center justify-center flex-grow h-full",
                {
                  " lg:flex": !isMobile && !toMobile,
                }
              )}
              ref={navBarRef}
              role="navigation"
            >
              {navigationItems.map((item, index) => {
                if (
                  !item ||
                  !item.navigationTitle ||
                  (!(item.redirectUrl === "/search") &&
                    !item.navigationItems &&
                    !item.redirectUrl)
                ) {
                  return null;
                }
                if (
                  item.redirectUrl === "/search" ||
                  item.navigationItems.length > 0
                ) {
                  return (
                    <div
                      key={index}
                      // role="menu"
                    >
                      <div className="relative flex h-8 items-center mx-1">
                        <NavButton
                          active={citiesVisible}
                          onClick={() => {
                            if (isToggleCities(item)) {
                              toggleCities(!citiesVisible);
                              setSecondaryNavBarOption(
                                secondaryNavBarOption === item.navigationTitle
                                  ? ""
                                  : item.navigationTitle
                              );
                              toggleSecondaryNavBar(false);
                              if (!citiesVisible) citiesListRef.current.focus();
                              else focusNextMenuOption();
                            } else {
                              toggleCities(false);
                              istoggleSecondaryNavBar(item.navigationTitle);
                              setOptions(item.navigationItems);
                              setTimeout(function () {
                                if (
                                  secondaryNavBarOption === item.navigationTitle
                                ) {
                                  let myActualOption = document.getElementById(
                                    item.navigationTitle
                                  );
                                  if (myActualOption) myActualOption.focus();
                                } else {
                                  if (secondNavRef && secondNavRef.current)
                                    secondNavRef.current.focus();
                                }
                              }, 500);
                            }
                            setExpandedOption(
                              !expandedOption ||
                                expandedOption !== item.navigationTitle
                                ? item.navigationTitle
                                : ""
                            );
                            setMenuOptionSelected(item.navigationTitle);
                          }}
                          key={item.navigationTitle}
                          className={cx(
                            "font-normal font-trade-gothic-20 uppercase",
                            {
                              "text-white": !hover,
                              "text-mattamy-blue": hover || isWhiteHeader,
                            }
                          )}
                          id={item.navigationTitle}
                          role={"button"}
                          tabIndex={"0"}
                          aria-expanded={
                            (citiesVisible && isToggleCities(item)) ||
                            (secondaryNavBarVisible &&
                              secondaryNavBarOption === item.navigationTitle)
                          }
                        >
                          <p className="flex">
                            {item.navigationTitle}{" "}
                            <ChevronDownIcon
                              className={cx(
                                "w-10-mattamy h-10-mattamy ml-1 mt-1",
                                {
                                  "text-white": !hover,
                                  "text-mattamy-blue": hover || isWhiteHeader,
                                }
                              )}
                            />
                          </p>
                        </NavButton>
                        <BlueBar
                          className={cx(
                            "absolute bottom-0 left-0 right-0 bg-mattamy-blue",
                            {
                              hidden:
                                !(
                                  (citiesVisible && isToggleCities(item)) ||
                                  (secondaryNavBarVisible &&
                                    secondaryNavBarOption ===
                                      item.navigationTitle)
                                ) || !secondaryNavBarOption,
                            }
                          )}
                        />
                      </div>
                      {isToggleCities(item) && (
                        <CityListContainer
                          className="hidden lg:flex bg-mattamy-blue w-full absolute left-0 right-0"
                          style={cityListanimationProps}
                          headerFixed={fixed}
                          isTop={isTop}
                          notificationActive={
                            notificationActive || nationalNotificationActive
                          }
                        >
                          <CitiesContainer
                            className="lg:flex items-start overflow-x-auto pt-16 pb-20"
                            tabIndex={"-1"}
                            ref={citiesListRef}
                            aria-hidden={citiesVisible ? false : true}
                          >
                            {cities.map((state, ci) => {
                              return (
                                <StateContainer key={state.state}>
                                  <h3 className="font-trade-gothic-20 text-2xl w-full text-white leading-9 tracking-tight">
                                    {state.doNotSearch ? (
                                      <span> {state.state} </span>
                                    ) : (
                                      <State
                                        aria-hidden="true"
                                        ref={(el) => {
                                          stateItemRef.current[ci] = el;
                                        }}
                                        role={"button"}
                                        as={"button"}
                                        rel="prefetch"
                                        href={state.href}
                                        onClick={() => {
                                          toggleCities(false);
                                          window.location.href = isMobile
                                            ? removehideMap(state.href)
                                            : state.href;
                                        }}
                                        onFocus={(e) => {
                                          e.target.setAttribute(
                                            "aria-hidden",
                                            "false"
                                          );
                                        }}
                                        onKeyDown={(e) => {
                                          if (
                                            !e.shiftKey &&
                                            e.key === "Tab" &&
                                            state.cities.length === 0
                                          ) {
                                            focusNextMenuOption();
                                          } else if (
                                            e.shiftKey &&
                                            e.key === "Tab" &&
                                            ci === 0
                                          ) {
                                            toggleCities(false);
                                          }
                                          if (e.keyCode === 27) {
                                            toggleCities(false);
                                          }
                                        }}
                                        key={state.state}
                                        tabIndex={citiesVisible ? "0" : "-1"}
                                        aria-label={
                                          "list with " +
                                          state.cities.length +
                                          " item " +
                                          state.state +
                                          ". Find my home."
                                        }
                                      >
                                        <div className="relative font-trade-gothic-20 text-2xl text-white leading-9 tracking-tight">
                                          {state.state}
                                          <LeftIconCity isState={true}>
                                            <LeftArrowIcon
                                              className={
                                                "transform text-aqua w-3 h-3 rotate-180"
                                              }
                                              id={"Header-" + state.state}
                                            />
                                          </LeftIconCity>
                                        </div>
                                      </State>
                                    )}
                                  </h3>
                                  <CityList>
                                    {state.cities.map((city, si) => {
                                      const CityLenght = countStat++;
                                      return (
                                        <City
                                          aria-hidden="true"
                                          tabIndex={citiesVisible ? 0 : "-1"}
                                          rel="prefetch"
                                          as={"button"}
                                          role={"button"}
                                          href={
                                            isMobile
                                              ? removehideMap(city.href)
                                              : city.href
                                          }
                                          ref={(el) => {
                                            cityItemRef.current[
                                              CityLenght
                                            ] = el;
                                          }}
                                          onClick={() => {
                                            toggleCities(false);
                                            window.location.href = isMobile
                                              ? removehideMap(city.href)
                                              : city.href;
                                          }}
                                          onKeyDown={(e) => {
                                            if (
                                              e.key === "Tab" &&
                                              !e.shiftKey
                                            ) {
                                              if (
                                                CityLenght ===
                                                countStat - 1
                                              ) {
                                                toggleCities(false);
                                                cityItemRef.current.forEach(
                                                  (element) => {
                                                    element.setAttribute(
                                                      "aria-hidden",
                                                      "true"
                                                    );
                                                  }
                                                );
                                              }
                                            }

                                            if (e.keyCode === 27) {
                                              stateItemRef.current[ci].focus();
                                              cityItemRef.current.forEach(
                                                (element) => {
                                                  element.setAttribute(
                                                    "aria-hidden",
                                                    "true"
                                                  );
                                                }
                                              );
                                            }
                                          }}
                                          onFocus={(e) => {
                                            e.target.setAttribute(
                                              "aria-hidden",
                                              "false"
                                            );
                                          }}
                                          key={city.name}
                                        >
                                          <div
                                            aria-hidden="false"
                                            aria-label={
                                              "View " +
                                              city.name +
                                              " information."
                                            }
                                            className="relative"
                                            role="menuitem"
                                          >
                                            {city.name}
                                            <LeftIconCity id="">
                                              <LeftArrowIcon
                                                className={
                                                  "transform text-aqua w-3 h-3 rotate-180"
                                                }
                                                id={"Header-" + city.name}
                                              />
                                            </LeftIconCity>
                                          </div>
                                        </City>
                                      );
                                    })}
                                  </CityList>
                                </StateContainer>
                              );
                            })}
                          </CitiesContainer>
                        </CityListContainer>
                      )}
                      {expandedOption == item.navigationTitle &&
                        options &&
                        !(isMobile || breakpoint === "md") && (
                          <SecondaryNavBar
                            className="hidden lg:flex bg-mattamy-blue w-full h-20 px-12 absolute left-0 right-0"
                            style={secondaryNavBarAnimationProps}
                            headerFixed={fixed}
                            isTop={isTop}
                            secondaryNavBarVisible={secondaryNavBarVisible}
                            notificationActive={
                              notificationActive || nationalNotificationActive
                            }
                          >
                            <div
                              className="w-full text-center items-center justify-around"
                              ref={secondNavRef}
                              //tabIndex={"-1"} //secondaryNavBarVisible ? "0" : "-1"}
                              aria-hidden={
                                secondaryNavBarVisible ? false : true
                              }
                            >
                              {options.map((option, i) => (
                                <SubNavigationOpt
                                  key={option.navigationTitle}
                                  ref={i === 0 ? firstLinkRef : null}
                                  role={"button"}
                                  rel="prefetch"
                                  href={option.redirectUrl}
                                  //aria-label={option.text}
                                  className="inline-block text-white tracking-tight font-trade-gothic-20 cursor-pointer"
                                  id={option.navigationTitle}
                                  tabIndex={secondaryNavBarVisible ? "0" : "-1"}
                                  onKeyDown={(e) => {
                                    if (
                                      options.length == i + 1 &&
                                      !e.shiftKey
                                    ) {
                                      toggleSecondaryNavBar(false);
                                      setExpandedOption(
                                        !expandedOption ||
                                          expandedOption !==
                                            item.navigationTitle
                                          ? item.navigationTitle
                                          : ""
                                      );
                                    }
                                    if (0 == i && e.shiftKey) {
                                      toggleSecondaryNavBar(false);
                                      setExpandedOption(
                                        !expandedOption ||
                                          expandedOption !==
                                            item.navigationTitle
                                          ? item.navigationTitle
                                          : ""
                                      );
                                    }
                                  }}
                                >
                                  <div role="menuitem">
                                    {option.navigationTitle}
                                  </div>
                                </SubNavigationOpt>
                              ))}
                            </div>
                          </SecondaryNavBar>
                        )}
                    </div>
                  );
                } else {
                  return (
                    <div
                      key={index}
                      className="relative flex h-8 items-center mx-4"
                    >
                      <Link
                        className={cx(
                          "font-normal font-trade-gothic-20 uppercase text-center",
                          {
                            "text-white": !hover,
                            "text-mattamy-blue": hover || isWhiteHeader,
                          }
                        )}
                        to={item.redirectUrl}
                        key={item.navigationTitle}
                        onClick={() => {
                          setSecondaryNavBarOption();
                          toggleSecondaryNavBar(false);
                          toggleCities(false);
                        }}
                        aria-label={item.navigationTitle}
                        id={item.navigationTitle}
                        // tabIndex={"0"}
                      >
                        <p //role="menuitem"
                        >
                          {item.navigationTitle}
                        </p>
                      </Link>
                      <BlueBar className="hidden absolute bottom-0 left-0 right-0 bg-mattamy-blue" />
                    </div>
                  );
                }
              })}
            </nav>

            <div
              className={cx(
                "hidden   ml-auto items-center justify-end border-gray-300 h-full",
                {
                  " lg:flex": !isMobile && !toMobile,
                }
              )}
            >
              <button
                className="flex h-8 w-8 items-center justify-center cursor-pointer"
                onClick={() => {
                  window.location.href = goToFavorites.href;
                }}
                onMouseEnter={() => setHeartFilled(true)}
                onMouseLeave={() => setHeartFilled(false)}
                aria-label="Go to Favorites Page"
                id={"goToFavoritesButton"}
              >
                {!heartFilled && (
                  <HeartIcon
                    className={cx({
                      "text-mattamy-blue": hover || isWhiteHeader,
                      "text-white": !hover && !isWhiteHeader,
                    })}
                  />
                )}
                {heartFilled && (
                  <HeartFilled className="h-6 w-6 text-mattamy-blue" />
                )}
              </button>
              <CountrySelectContainer
                onKeyUp={(e) => {
                  if (e.shiftKey && e.key === "Tab") {
                    scrollToTop();
                  }
                }}
                className="flex items-center justify-center ml-4 mr-2 h-full"
              >
                <CountrySelect
                  isMobile={isMobile}
                  id={"countryContainer"}
                  idOption={"countrySelectorOption"}
                  tabIndex="0"
                  color={hover || isWhiteHeader ? "blue" : "white"}
                  onKeyDown={(e) => {
                    e.nativeEvent.stopPropagation();
                    e.stopPropagation();
                    if (!e.shiftKey && e.key === "Tab") {
                      const firstElement = getFirstContentElement();
                      if (firstElement) {
                        firstElement.focus();
                      } else {
                        const root = document.querySelector("#root");
                        const sections = root.querySelectorAll("section");
                        sections[0].focus();
                      }
                    }
                    if (e.shiftKey && e.key === "Tab") {
                      let goToFavoritesButton = document.getElementById(
                        "goToFavoritesButton"
                      );
                      if (goToFavoritesButton) goToFavoritesButton.focus();
                      else if (navBarRef && navBarRef.current)
                        navBarRef.current.focus();
                    }
                  }}
                />
              </CountrySelectContainer>
            </div>

            <button
              className={cx("w-6 h-6  flex justify-end items-center", {
                "text-white": !hover,
                "text-mattamy-blue": hover || isWhiteHeader,
                "lg:hidden": !isMobile && !toMobile,
              })}
              tabIndex="0"
              onClick={(e) => {
                toggleRightDrawer(true);
                setTimeout(() => {
                  const closeMobileNavigationMenuButton = document.getElementById(
                    "closeNavigationMenu"
                  );
                  if (closeMobileNavigationMenuButton) {
                    e.preventDefault();
                    closeMobileNavigationMenuButton.focus();
                  }
                }, 500);
              }}
              aria-label="Navigation menu."
              aria-haspopup="dialog"
              id="MobileNavigationMenu"
            >
              <MenuIcon className={"w-6 mt-1"} />
            </button>
          </StyledContainer>
        </HeaderContainer>
      </div>

      {((!isMobile && toMobile) || isMobile || breakpoint === "md") &&
        isRightDrawerOpen && (
          <animated.div
            style={rightDrawerAnimationProps}
            className="w-screen min-h-screen h-screen overflow-x-hidden bg-white fixed top-0"
            aria-modal="true"
            role="dialog"
            aria-hidden={!isRightDrawerOpen}
          >
            <div className="flex items-center justify-between w-full h-24 px-6 py-2 shadow-lg">
              <a
                rel="prefetch"
                href="/"
                aria-label={logoAlt}
                id="logoNavigationMenu"
                onKeyDown={(e) => {
                  if (e.shiftKey && e.key === "Tab") {
                    e.preventDefault();
                    let goToFavoritesButton = document.getElementById(
                      "favoritesMobileButton"
                    );
                    if (goToFavoritesButton) goToFavoritesButton.focus();
                  }
                }}
              >
                <figure>
                  <Logo src={logoSrc} alt={logoAlt} />
                </figure>
              </a>

              <button
                id="closeNavigationMenu"
                className="w-8 h-8 text-action-blue"
                onClick={(e) => {
                  toggleRightDrawer(false);
                  const mobileMenu = document.getElementById(
                    "MobileNavigationMenu"
                  );
                  if (mobileMenu) {
                    e.preventDefault();
                    mobileMenu.focus();
                  }
                }}
                aria-label="Close navigation menu"
              >
                <svg viewBox="0 0 24 24" fill="none">
                  <path
                    fill="currentColor"
                    d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19
                12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z"
                  />
                </svg>
              </button>
            </div>
            <div className="relative w-full">
              <div className="flex items-center justify-between w-full h-20 px-6 py-2 bg-mattamy-gray-bg">
                <CountrySelectText className="font-trade-gothic-20 text-base text-mattamy-blue font-bold uppercase leading-5 tracking-wide">
                  {"SELECT A COUNTRY"}
                </CountrySelectText>
                <CountrySelect
                  isMobile={isMobile}
                  color={
                    isMobile
                      ? "white"
                      : hover || isWhiteHeader
                      ? "blue"
                      : "white"
                  }
                  id={"countryContainerMobile"}
                  idOption={"countrySelectorOptionMobile"}
                />
              </div>

              <div
                id="mobileNavigationItems"
                className="relative w-full h-full"
              >
                {navigationItems.map((item, index) => {
                  if (
                    !item ||
                    !item.navigationTitle ||
                    (!(item.redirectUrl === "/search") &&
                      !item.navigationItems &&
                      !item.redirectUrl)
                  ) {
                    return null;
                  }
                  return (
                    <div
                      key={item.navigationTitle}
                      className="flex flex-col items-center justify-start w-full px-6 focus:outline-5px auto -webkit-focus-ring-color"
                    >
                      {item.redirectUrl === "/search" ||
                      (item.navigationItems &&
                        item.navigationItems.length &&
                        item.navigationItems.length > 0) ? (
                        <>
                          <button
                            className="flex items-center justify-between w-full z-40 focus:outline-5px auto -webkit-focus-ring-color"
                            onClick={() => {
                              if (isToggleCities(item)) {
                                toggleCitiesInDrawer(!citiesVisibleInDrawer);
                                toggleSecondaryNavBarInDrawer(false);
                              } else {
                                toggleSecondaryNavBarInDrawer(true);
                                toggleCitiesInDrawer(false);
                              }
                              setOptions(item.navigationItems);
                              setExpandedOption(
                                !expandedOption ||
                                  expandedOption !== item.navigationTitle
                                  ? item.navigationTitle
                                  : ""
                              );
                            }}
                            role="button"
                            aria-expanded={
                              expandedOption === item.navigationTitle
                            }
                          >
                            <p className="font-trade-gothic-20 text-xl text-mattamy-blue px-2 py-4 font-bold uppercase leading-normal">
                              {item.navigationTitle}
                            </p>

                            <svg
                              className={cx(
                                "w-6 h-6 text-mattamy-blue transform",
                                {
                                  "-rotate-90":
                                    expandedOption === item.navigationTitle,
                                  "rotate-180":
                                    expandedOption !== item.navigationTitle,
                                }
                              )}
                              viewBox="0 0 24 24"
                              fill="none"
                            >
                              <path
                                fill="currentColor"
                                d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"
                              />
                            </svg>
                          </button>
                          <div
                            className="w-full  z-20"
                            aria-hidden={
                              expandedOption !== item.navigationTitle
                            }
                            // role={"menu"}
                          >
                            {isToggleCities(item) &&
                              expandedOption === item.navigationTitle && (
                                <animated.div style={expand}>
                                  {renderCities()}
                                </animated.div>
                              )}
                            {expandedOption === item.navigationTitle &&
                              options && (
                                <animated.div style={expandOptions}>
                                  {renderOptions(options)}
                                </animated.div>
                              )}
                          </div>
                          <RectangleSeparator className="w-full bg-light-blue mb-2" />
                        </>
                      ) : (
                        <div className="flex flex-col w-full z-40">
                          <a
                            className=" font-trade-gothic-20 text-xl text-mattamy-blue px-2 py-4 font-bold uppercase leading-normal focus:outline-5px auto -webkit-focus-ring-color"
                            href={item.redirectUrl}
                            aria-label={item.navigationTitle}
                            role="link"
                          >
                            <p>{item.navigationTitle}</p>
                          </a>
                          <RectangleSeparator className="bg-light-blue" />
                        </div>
                      )}
                    </div>
                  );
                })}
                <div className="flex w-full items-center justify-between pl-6 pr-6">
                  <a
                    className="font-trade-gothic-20 text-xl text-mattamy-blue px-2 py-4 font-bold uppercase leading-normal w-full"
                    href={goToFavorites.href}
                    aria-label={"Go to favorites page"}
                    onKeyDown={(e) => {
                      if (!e.shiftKey && e.key === "Tab") {
                        e.preventDefault();
                        let goToCloseButton = document.getElementById(
                          "logoNavigationMenu"
                        );
                        if (goToCloseButton) goToCloseButton.focus();
                      }
                    }}
                    id="favoritesMobileButton"
                  >
                    <HeartFilledIcon className="h-6 w-6 text-action-blue" />
                  </a>
                </div>
              </div>
            </div>
          </animated.div>
        )}

      <Helmet>
        <script defer type="text/javascript">
          {`
            window._mfq = window._mfq || [];
            (function() {
              var mf = document.createElement("script");
              mf.type = "text/javascript"; mf.defer = true;
              mf.src = "${mouseflowTrackingURL}";
              document.getElementsByTagName("head")[0].appendChild(mf);
            })();
        `}
        </script>
      </Helmet>

      <Helmet>
        <script
          defer
          src="https://cdn.cookielaw.org/scripttemplates/otSDKStub.js"
          type="text/javascript"
          charset="UTF-8"
          //data-domain-script="d3641430-9167-4971-922c-4d0ffe4e0654-test"
          data-domain-script="bac8dc2f-1e7e-4a04-aca9-795d67d42e1c"
        ></script>
        <script defer type="text/javascript">
          {`function OptanonWrapper() { }`}
        </script>
        <div id="optanon-cookie-policy"></div>
        <div id="ot-sdk-cookie-policy"></div>
      </Helmet>

      {currentPageType !== pageTypes.MetroSearch && (
        <ScrollToTopButton pageType={currentPageType} />
      )}
      <div ref={dynamicsScriptsCodeRef}></div>
    </>
  );
};

export default withRouter(Header);
